import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import AuthLoading from "components/common/AuthLoading";
import { useAuth } from "contexts/AuthContext";
import { useWorkspace } from "contexts/WorkspaceContext";

const DEFAULT_ROLES = ["care_owner", "care_admin", "auto_owner", "auto_admin"];

const normalizeRole = (role) =>
  String(role || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const RequireCareRole = ({ children, roles = DEFAULT_ROLES, redirectTo = "/dashboard" }) => {
  const { careRoles = [], refreshCareRoles } = useAuth();
  const { selectedWorkspaceId, workspaceListReady } = useWorkspace();
  const [checking, setChecking] = useState(Boolean(refreshCareRoles));
  const [checkedRoles, setCheckedRoles] = useState(null);
  const location = useLocation();

  useEffect(() => {
    let active = true;
    if (!refreshCareRoles) {
      setChecking(false);
      return () => {
        active = false;
      };
    }
    if (!workspaceListReady) {
      setChecking(true);
      return () => {
        active = false;
      };
    }

    setChecking(true);
    setCheckedRoles(null);
    refreshCareRoles({ workspaceId: selectedWorkspaceId, wid: selectedWorkspaceId })
      .then((nextRoles) => {
        if (active) setCheckedRoles(Array.isArray(nextRoles) ? nextRoles : []);
      })
      .catch(() => {
        if (active) setCheckedRoles([]);
      })
      .finally(() => {
        if (active) setChecking(false);
      });

    return () => {
      active = false;
    };
  }, [refreshCareRoles, selectedWorkspaceId, workspaceListReady]);

  if (checking || !workspaceListReady) return <AuthLoading />;

  const rolesToCheck = checkedRoles || careRoles;
  const normalizedCareRoles = new Set(rolesToCheck.map(normalizeRole).filter(Boolean));
  const allowed = roles.some((role) => normalizedCareRoles.has(normalizeRole(role)));
  if (!allowed) {
    return <Navigate to={redirectTo} replace state={{ from: location }} />;
  }

  return children;
};

export default RequireCareRole;
